import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { markHubStage, clearApplySession, applicationFormPath } from '../../lib/screeningApplySession';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import {
  DocumentTextIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import { useCandidateLanguage } from '../../i18n/candidate';

const ScreeningHub = () => {
  const { screeningId } = useParams();
  const t = useCandidateLanguage({ screeningId });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(true, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningLeaveWarning(!!screeningId);
  const emailFromQuery = searchParams.get('email') || '';
  const nameFromQuery = searchParams.get('name') || '';

  // No session-end beacon on hub: refresh would force-complete assessment/interview prematurely.

  const [hubInfo, setHubInfo] = useState(null);
  const [skippingDocs, setSkippingDocs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [linkInactive, setLinkInactive] = useState(false);
  useScreeningExpiryRedirect({
    screeningId,
    email: emailFromQuery,
    enabled: !!screeningId,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchHubInfo = async () => {
      try {
        const params = emailFromQuery ? `?email=${encodeURIComponent(emailFromQuery)}` : '';
        const res = await axios.get(`/api/screenings/${screeningId}/apply-info${params}`);
        if (!cancelled) setHubInfo(res.data);
      } catch (e) {
        if (!cancelled) {
          const isInactive = e.response?.status === 410 && e.response?.data?.code === 'LINK_INACTIVE';
          setLinkInactive(!!isInactive);
          setError(isInactive ? '' : (e.response?.data?.message || 'Failed to load screening.'));
          setHubInfo(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (screeningId) fetchHubInfo();
    return () => {
      cancelled = true;
    };
  }, [screeningId, emailFromQuery]);

  useEffect(() => {
    if (!hubInfo || !screeningId) return;
    if (hubInfo.rejected) {
      clearApplySession(screeningId);
      const thankYouParams = new URLSearchParams();
      if (emailFromQuery) thankYouParams.set('email', emailFromQuery.trim());
      if (nameFromQuery) thankYouParams.set('name', nameFromQuery.trim());
      thankYouParams.set('rejected', 'true');
      const qs = thankYouParams.toString();
      navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
      return;
    }
    markHubStage(screeningId, emailFromQuery, nameFromQuery);
  }, [hubInfo, screeningId, emailFromQuery, nameFromQuery, navigate]);

  const hasAssessment = !!hubInfo?.hasAssessment && !!hubInfo?.assessmentSharingLink;
  const hasInterview = !!hubInfo?.hasInterview && !!hubInfo?.interviewSharingLink;
  const hasDocumentCollection = !!hubInfo?.includeDocumentCollection && !!hubInfo?.documentCollectionToken;
  const hasApplicationForm = !!hubInfo?.includeApplicationForm && !!hubInfo?.applicationFormToken;
  const hasBoth = hasAssessment && hasInterview;

  // Redirect to thank-you when all configured modules are completed (prevent re-access)
  useEffect(() => {
    if (!hubInfo || (!hasAssessment && !hasInterview && !hasDocumentCollection && !hasApplicationForm)) return;
    const documentCollectionDone = !hasDocumentCollection || hubInfo.documentCollectionCompleted;
    const applicationFormDone = !hasApplicationForm || hubInfo.applicationFormCompleted;
    const assessmentDone = !hasAssessment || hubInfo.assessmentCompleted;
    const interviewDone = !hasInterview || hubInfo.interviewCompleted;
    if ((documentCollectionDone && applicationFormDone && assessmentDone && interviewDone) || hubInfo.completed) {
      const thankYouParams = new URLSearchParams();
      if (emailFromQuery) thankYouParams.set('email', emailFromQuery);
      if (nameFromQuery) thankYouParams.set('name', nameFromQuery);
      const qs = thankYouParams.toString();
      navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
    }
  }, [hubInfo, hasAssessment, hasInterview, hasDocumentCollection, hasApplicationForm, screeningId, navigate, emailFromQuery, nameFromQuery]);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const hubQuery = new URLSearchParams();
  if (emailFromQuery) hubQuery.set('email', emailFromQuery);
  if (nameFromQuery) hubQuery.set('name', nameFromQuery);
  const hubQueryStr = hubQuery.toString();
  const hubPath = `/apply/${screeningId}/hub${hubQueryStr ? `?${hubQueryStr}` : ''}`;
  const returnUrl = baseUrl + hubPath;

  const assessmentQuery = new URLSearchParams();
  let assessmentReturnUrl = returnUrl;
  if (hasAssessment && hasInterview && hubInfo.interviewSharingLink) {
    const interviewQuery = new URLSearchParams();
    interviewQuery.set('returnUrl', returnUrl);
    interviewQuery.set('screeningId', screeningId);
    if (emailFromQuery) interviewQuery.set('email', emailFromQuery);
    if (nameFromQuery) interviewQuery.set('name', nameFromQuery);
    interviewQuery.set('hasAssessment', 'true');
    assessmentReturnUrl = `${baseUrl}/ai-interview/${hubInfo.interviewSharingLink}?${interviewQuery.toString()}`;
  }
  assessmentQuery.set('returnUrl', assessmentReturnUrl);
  assessmentQuery.set('screeningId', screeningId);
  if (emailFromQuery) assessmentQuery.set('email', emailFromQuery);
  if (nameFromQuery) assessmentQuery.set('name', nameFromQuery);
  if (hasInterview) {
    assessmentQuery.set('hasInterview', 'true');
    if (hubInfo.interviewSharingLink) {
      assessmentQuery.set('interviewSharingLink', hubInfo.interviewSharingLink);
    }
  }
  const assessmentUrl = hasAssessment
    ? `${baseUrl}/assessment/${hubInfo.assessmentSharingLink}?${assessmentQuery.toString()}`
    : null;
  const interviewQuery = new URLSearchParams();
  interviewQuery.set('returnUrl', returnUrl);
  interviewQuery.set('screeningId', screeningId);
  if (emailFromQuery) interviewQuery.set('email', emailFromQuery);
  if (nameFromQuery) interviewQuery.set('name', nameFromQuery);
  if (hasAssessment) interviewQuery.set('hasAssessment', 'true');
  const interviewUrl = hasInterview
    ? `${baseUrl}/ai-interview/${hubInfo.interviewSharingLink}?${interviewQuery.toString()}`
    : null;

  const docQuery = new URLSearchParams();
  docQuery.set('token', hubInfo?.documentCollectionToken || '');
  if (emailFromQuery) docQuery.set('email', emailFromQuery);
  if (nameFromQuery) docQuery.set('name', nameFromQuery);
  const documentCollectionUrl = hasDocumentCollection
    ? `/apply/${screeningId}/documents?${docQuery.toString()}`
    : null;
  const applicationFormUrl = hasApplicationForm
    ? applicationFormPath(screeningId, hubInfo.applicationFormToken, { email: emailFromQuery, name: nameFromQuery })
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4">
        <div className="text-gray-500 text-sm">{t('Loading…')}</div>
      </div>
    );
  }

  if (linkInactive) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
            <LinkIcon className="h-6 w-6 text-amber-600" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{t('This application link is no longer active')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('The recruiter has disabled this link, so applications are no longer being accepted. If you believe this is an error, please contact the recruiter or company directly.')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !hubInfo) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <p className="text-red-600 text-sm">{error ? t(error) : t('Screening not found.')}</p>
        </div>
      </div>
    );
  }

  if (!hasAssessment && !hasInterview && !hasDocumentCollection && !hasApplicationForm) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm">
          <p className="text-gray-600 text-sm">
            {t('No assessment, interview, or document submission steps for this screening.')}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            {t("You're done. We'll be in touch if your profile matches.")}
          </p>
        </div>
      </div>
    );
  }

  const documentCollectionDone = !hasDocumentCollection || hubInfo.documentCollectionCompleted;
  const applicationFormDone = !hasApplicationForm || hubInfo.applicationFormCompleted;
  const assessmentDone = !hasAssessment || hubInfo.assessmentCompleted;
  const interviewDone = !hasInterview || hubInfo.interviewCompleted;

  /** Order: documents → application form → assessment → interview. Each unlocks the next. */
  const documentsPending = hasDocumentCollection && !hubInfo.documentCollectionCompleted;
  const applicationFormPending = hasApplicationForm && !hubInfo.applicationFormCompleted;
  const assessmentLockedPendingDocuments = documentsPending || applicationFormPending;
  const interviewLockedPendingDocuments = documentsPending || applicationFormPending;
  const interviewLockedPendingAssessment = hasBoth && !hubInfo.assessmentCompleted;
  const earlierStepMessage = documentsPending
    ? t('Complete document collection first to unlock this step.')
    : t('Complete the application form first to unlock this step.');

  const allCompleted =
    (documentCollectionDone && applicationFormDone && assessmentDone && interviewDone) || hubInfo?.completed;

  if (allCompleted) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4">
        <div className="text-gray-500 text-sm">{t('Redirecting…')}</div>
      </div>
    );
  }

  const screeningDeadline = hubInfo?.screeningDeadline || null;
  const timerExpired =
    !!hubInfo?.timeUp ||
    (screeningDeadline
      ? (() => {
          const parsed = Date.parse(screeningDeadline);
          return !Number.isNaN(parsed) && Date.now() >= parsed;
        })()
      : false);

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-8">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title="Leave this page?"
        message="Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out."
      />
      <div className="mx-auto max-w-4xl">
        <header className="flex items-center justify-between mb-4 sm:mb-8">
          <div className="flex flex-col">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {t('Powered by')}
            </p>
            <div className="mt-0.5 flex items-center gap-1">
              <img
                src="/logo.svg"
                alt="TalentCrux"
                className="h-14 w-14 object-contain flex-shrink-0"
              />
              <span className="text-2xl font-bold text-gray-900">TalentCrux</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
        </header>

        {/* Stepper container classes */}
        {(() => {
          const activeCount =
            (hasAssessment ? 1 : 0) + (hasInterview ? 1 : 0) + (hasDocumentCollection ? 1 : 0) + (hasApplicationForm ? 1 : 0);
          const isGrid = activeCount > 1;
          return (
            <div
              className={`w-full rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden mx-auto ${
                activeCount > 1 ? 'max-w-4xl' : 'max-w-md'
              }`}
            >
              <div className="border-b border-gray-200 px-6 py-5">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t('Screening steps')}
                </p>
                <h1 className="mt-1 text-lg font-semibold text-gray-900">{hubInfo.jobTitle}</h1>
                <p className="mt-1 text-xs text-gray-500">{hubInfo.screeningName}</p>
                <p className="mt-3 text-sm text-gray-600">
                  {activeCount > 1
                    ? t("Complete each step below to submit your application. You'll return here after each step.")
                    : t("Complete the step below to submit your application. You'll return here when done.")}
                </p>
              </div>

              <div
                className={`p-6 ${
                  activeCount === 3
                    ? 'grid grid-cols-1 md:grid-cols-3 gap-6'
                    : isGrid
                    ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
                    : 'flex justify-center'
                }`}
              >
          {hasAssessment && (
            <div
              className={`rounded-xl border border-gray-200 bg-gray-50/60 p-5 flex flex-col ${
                !hasBoth ? 'max-w-sm w-full' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-electric-blue-50">
                  <DocumentTextIcon className="h-5 w-5 text-electric-blue" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  {hubInfo.assessmentTitle || t('Assessment')}
                </h2>
              </div>
              {hubInfo.assessmentDurationMinutes != null && (
                <p className="text-xs text-gray-500 mb-4">
                  {t('Duration: {minutes} min', { minutes: hubInfo.assessmentDurationMinutes })}
                </p>
              )}
              <p className="text-sm text-gray-600 mb-6 flex-1">
                {hubInfo.assessmentCompleted
                  ? t('You have completed this assessment.')
                  : assessmentLockedPendingDocuments
                  ? earlierStepMessage
                  : t('Complete and submit the assessment.')}
              </p>
              {hubInfo.assessmentCompleted ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-500 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <CheckCircleIcon className="h-5 w-5 text-lavender-purple" />
                  {t('Completed')}
                </span>
              ) : assessmentLockedPendingDocuments ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed border border-gray-200"
                  aria-disabled="true"
                  title={earlierStepMessage}
                >
                  {t('Take assessment')}
                </span>
              ) : timerExpired ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed"
                  aria-disabled="true"
                >
                  {t("Time's up")}
                </span>
              ) : (
                <a
                  href={assessmentUrl}
                  className="inline-flex items-center justify-center rounded-lg bg-electric-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors"
                >
                  {t('Take assessment')}
                </a>
              )}
            </div>
          )}

          {hasInterview && (
            <div
              className={`rounded-xl border border-gray-200 bg-gray-50/60 p-5 flex flex-col ${
                !hasBoth ? 'max-w-sm w-full' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-electric-blue-50">
                  <VideoCameraIcon className="h-5 w-5 text-electric-blue" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  {hubInfo.interviewTitle || t('AI Interview')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-6 flex-1">
                {hubInfo.interviewCompleted
                  ? t('You have completed this interview.')
                  : interviewLockedPendingDocuments
                  ? earlierStepMessage
                  : interviewLockedPendingAssessment
                  ? t('Complete the assessment first to unlock this step.')
                  : t('Complete the AI interview.')}
              </p>
              {hubInfo.interviewCompleted ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-500 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <CheckCircleIcon className="h-5 w-5 text-lavender-purple" />
                  {t('Completed')}
                </span>
              ) : interviewLockedPendingDocuments ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed border border-gray-200"
                  aria-disabled="true"
                  title={earlierStepMessage}
                >
                  {t('Take interview')}
                </span>
              ) : interviewLockedPendingAssessment ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed border border-gray-200"
                  aria-disabled="true"
                  title={t('Complete the assessment first')}
                >
                  {t('Take interview')}
                </span>
              ) : timerExpired ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed"
                  aria-disabled="true"
                >
                  {t("Time's up")}
                </span>
              ) : (
                <a
                  href={interviewUrl}
                  className="inline-flex items-center justify-center rounded-lg bg-electric-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors"
                >
                  {t('Take interview')}
                </a>
              )}
            </div>
          )}

          {hasDocumentCollection && (
            <div
              className={`rounded-xl border border-gray-200 bg-gray-50/60 p-5 flex flex-col ${
                activeCount === 1 ? 'max-w-sm w-full' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-electric-blue-50">
                  <DocumentTextIcon className="h-5 w-5 text-electric-blue" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  {t('Documents')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-6 flex-1">
                {hubInfo.documentCollectionCompleted
                  ? t('You have completed this step.')
                  : t('Upload the requested documents to unlock the next steps.')}
              </p>
              {hubInfo.documentCollectionCompleted ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-500 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <CheckCircleIcon className="h-5 w-5 text-lavender-purple" />
                  {t('Completed')}
                </span>
              ) : timerExpired ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed"
                  aria-disabled="true"
                >
                  {t("Time's up")}
                </span>
              ) : (
                <a
                  href={documentCollectionUrl}
                  className="inline-flex items-center justify-center rounded-lg bg-electric-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors"
                >
                  {t('Upload documents')}
                </a>
              )}
            </div>
          )}

          {hasApplicationForm && (
            <div
              className={`rounded-xl border border-gray-200 bg-gray-50/60 p-5 flex flex-col ${
                activeCount === 1 ? 'max-w-sm w-full' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-electric-blue-50">
                  <ClipboardDocumentListIcon className="h-5 w-5 text-electric-blue" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">{t('Application form')}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6 flex-1">
                {hubInfo.applicationFormCompleted
                  ? t('You have submitted your application form.')
                  : documentsPending
                  ? t('Complete document collection first to unlock this step.')
                  : t('Fill in your personal, education and work details. Your answers save as you type.')}
              </p>
              {hubInfo.applicationFormCompleted ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-500 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <CheckCircleIcon className="h-5 w-5 text-lavender-purple" />
                  {t('Completed')}
                </span>
              ) : documentsPending ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed border border-gray-200"
                  aria-disabled="true"
                  title={t('Complete document collection first')}
                >
                  {t('Fill application form')}
                </span>
              ) : timerExpired ? (
                <span
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-400 cursor-not-allowed"
                  aria-disabled="true"
                >
                  {t("Time's up")}
                </span>
              ) : (
                <a
                  href={applicationFormUrl}
                  className="inline-flex items-center justify-center rounded-lg bg-electric-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white transition-colors"
                >
                  {t('Fill application form')}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      );
    })()}
      </div>
    </div>
  );
};

export default ScreeningHub;
